import React, { useRef, useEffect, useState } from 'react';
import * as faceapi from 'face-api.js';
import { loadModels } from '../screens/public/face/faceUtils';

import { apiRequest } from '../service/apiRequest';
const FaceIdentify = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const intervalRef = useRef(null);
  const faceMatcherRef = useRef(null);
  const onPlayRef = useRef(null);
  const [message, setMessage] = useState('Waiting for face detection...');

  useEffect(() => {
    let isMounted = true;

    const runFaceRecognitionLoop = async () => {
      if (!videoRef.current || !canvasRef.current || !faceMatcherRef.current) return;

      const canvas = canvasRef.current;
      const displaySize = {
        width: videoRef.current.width,
        height: videoRef.current.height,
      };
      faceapi.matchDimensions(canvas, displaySize);

      intervalRef.current = setInterval(async () => {
        try {
          const detections = await faceapi
            .detectAllFaces(videoRef.current)
            .withFaceLandmarks()
            .withFaceDescriptors();

          // Clear canvas every time before drawing
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          if (detections.length === 0) {
            setMessage('No faces detected');
            return;
          } else {
            setMessage('');
          }

          const resizedDetections = faceapi.resizeResults(detections, displaySize);
          let anyMatchFound = false;

          resizedDetections.forEach(detection => {
            const bestMatch = faceMatcherRef.current.findBestMatch(detection.descriptor);
            const box = detection.detection.box;
            const drawBox = new faceapi.draw.DrawBox(box, { label: bestMatch.toString() });
            drawBox.draw(canvas);

            if (bestMatch.label !== 'unknown') {
              anyMatchFound = true;
            }
          });

          setMessage(anyMatchFound ? 'Face matched successfully!' : 'No matching face found.');
        } catch (err) {
          console.error('Error during detection:', err);
          setMessage('Error during face detection');
        }
      }, 1000);
    };

    const startVideo = async () => {
      try {
        setMessage('Requesting webcam access...');
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (videoRef.current && isMounted) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setMessage('Video started');
          return true;
        } else {
          setMessage('Video element not ready');
        }
      } catch (err) {
        console.error('Webcam error:', err);
        setMessage('Error accessing webcam');
      }
      return false;
    };

    const loadAndStartRecognition = async () => {
      try {
        setMessage('Loading models...');
        await loadModels();

        setMessage('Loading face data from API...');

        const response = await apiRequest({
                method: 'get',
                url: 'faces',
              });

        if (!response) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error(`Expected JSON, got ${contentType}`);
        }

        const facesData = await response.json();

        if (!facesData || facesData.length === 0) {
          throw new Error('No face data received from API');
        }

        const labeledDescriptors = facesData
          .map(user => {
            try {
              const parsed = JSON.parse(user.descriptors);
              const floatDescriptors = parsed.map(desc => new Float32Array(desc));
              return new faceapi.LabeledFaceDescriptors(user.label, floatDescriptors);
            } catch (err) {
              console.warn(`Invalid descriptor for user "${user.label}"`, err);
              return null;
            }
          })
          .filter(Boolean);

        if (labeledDescriptors.length === 0) {
          throw new Error('No valid labeled face descriptors found');
        }

        faceMatcherRef.current = new faceapi.FaceMatcher(labeledDescriptors, 0.6);

        if (videoRef.current) {
          if (onPlayRef.current) {
            videoRef.current.removeEventListener('play', onPlayRef.current);
          }
          onPlayRef.current = () => {
            runFaceRecognitionLoop();
          };
          videoRef.current.addEventListener('play', onPlayRef.current);
        }

        setMessage('Ready for face detection');
      } catch (err) {
        console.error('Recognition setup failed:', err);
        setMessage('Failed to initialize face recognition: ' + err.message);
      }
    };

    // Wait for videoRef to be set before starting
    const delayedStart = async () => {
      if (!videoRef.current) {
        setTimeout(delayedStart, 100);
        return;
      }
      const success = await startVideo();
      if (success && isMounted) {
        await loadAndStartRecognition();
      }
    };

    delayedStart();

    return () => {
      isMounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (videoRef.current) {
        if (onPlayRef.current) videoRef.current.removeEventListener('play', onPlayRef.current);
        if (videoRef.current.srcObject) {
          videoRef.current.srcObject.getTracks().forEach(track => track.stop());
        }
      }
    };
  }, []);

  return (
    <div style={{ position: 'relative', width: 720, height: 560 }}>
      <video
        ref={videoRef}
        autoPlay
        muted
        width={720}
        height={560}
        style={{ position: 'absolute' }}
      />
      <canvas
        ref={canvasRef}
        width={720}
        height={560}
        style={{ position: 'absolute' }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: 10,
          width: '100%',
          textAlign: 'center',
          color: 'white',
          fontWeight: 'bold',
          textShadow: '0 0 5px black',
          fontSize: '18px',
        }}
      >
        {message}
      </div>
    </div>
  );
};

export default FaceIdentify;
